/**
 * format.ts — single source of truth for date/time rendering across the admin web.
 *
 * Locale: en-IN. Default timezone: Asia/Kolkata (IST) — overridable per-call so a
 * society-scoped TZ from settings can be passed in later without further refactor.
 *
 * Invariants:
 *  - All formatters accept Date | string | null | undefined.
 *  - Null / undefined / unparseable values render as the em-dash '—' (never throw).
 *  - All formatters use Intl.DateTimeFormat (no native toLocale* drift).
 *  - formatDate     → "10 Jun 2026"
 *  - formatDateTime → "10 Jun 2026, 4:45 pm"
 *  - formatRelative → "just now" / "2 minutes ago" / "yesterday" / "10 Jun"
 */

const DEFAULT_LOCALE = 'en-IN';
const DEFAULT_TZ = 'Asia/Kolkata';
const EMPTY = '—';

export interface FormatOptions {
  /** IANA timezone (e.g. "Asia/Kolkata", "America/New_York"). Defaults to Asia/Kolkata. */
  timeZone?: string;
  /** BCP-47 locale tag. Defaults to en-IN. */
  locale?: string;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * "10 Jun 2026" — date only, en-IN, IST by default.
 */
export function formatDate(
  value: Date | string | null | undefined,
  opts: FormatOptions = {}
): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  return new Intl.DateTimeFormat(opts.locale ?? DEFAULT_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: opts.timeZone ?? DEFAULT_TZ
  }).format(d);
}

/**
 * "10 Jun 2026, 4:45 pm" — date + 12h time, en-IN, IST by default.
 */
export function formatDateTime(
  value: Date | string | null | undefined,
  opts: FormatOptions = {}
): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  return new Intl.DateTimeFormat(opts.locale ?? DEFAULT_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: opts.timeZone ?? DEFAULT_TZ
  }).format(d);
}

/**
 * Compact human relative time:
 *   < 1 min   → "just now"
 *   < 60 min  → "N minutes ago" / "1 minute ago"
 *   < 24 hr   → "N hours ago" / "1 hour ago"
 *   = 1 day   → "yesterday"
 *   < 7 days  → "N days ago"
 *   else      → "10 Jun" (locale + IST date)
 */
export function formatRelative(
  value: Date | string | null | undefined,
  opts: FormatOptions = {}
): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  return new Intl.DateTimeFormat(opts.locale ?? DEFAULT_LOCALE, {
    day: 'numeric',
    month: 'short',
    timeZone: opts.timeZone ?? DEFAULT_TZ
  }).format(d);
}

/**
 * "YYYY-MM-DD" in the configured TZ — for <input type="date"> defaults and
 * day-bucket query params.
 */
export function todayIsoDate(opts: FormatOptions = {}): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: opts.timeZone ?? DEFAULT_TZ
  }).format(new Date());
  // en-CA already emits YYYY-MM-DD
  return parts;
}
